'use client';

import { DollarSign, GraduationCap, Globe, Clock, Users, Wallet } from 'lucide-react';
import { AiSpendPageBody } from '@/domains/pages/ai-spend/ai-spend-page-body';
import { AiSpendSegmentPageBody } from '@/domains/pages/ai-spend/ai-spend-segment-page-body';
import { AiSpendPageLoadingSkeleton } from '@/domains/pages/ai-spend/ai-spend-page-loading';
import { createAiSpendSegmentPageLoadingSkeleton } from '@/domains/pages/ai-spend/ai-spend-segment-page-loading';
import { AiProficiencyPageBody } from '@/domains/pages/ai-proficiency/ai-proficiency-page-body';
import { AiProficiencySegmentPageBody } from '@/domains/pages/ai-proficiency/ai-proficiency-segment-page-body';
import { AiProficiencyPageLoadingSkeleton } from '@/domains/pages/ai-proficiency/ai-proficiency-page-loading';
import { createAiProficiencySegmentPageLoadingSkeleton } from '@/domains/pages/ai-proficiency/ai-proficiency-segment-page-loading';
import { UpskillingPageBody } from '@/domains/pages/ai-proficiency/upskilling-page-body';
import { UpskillingPageLoadingSkeleton } from '@/domains/pages/ai-proficiency/upskilling-page-loading';
import { ModelSavingsPageBody } from '@/domains/pages/model-savings/model-savings-page-body';
import { ModelSavingsPageLoadingSkeleton } from '@/domains/pages/model-savings/model-savings-page-loading';
import { NEBULY_PAGE_ROUTES } from '@/domains/pages/constants';
import {
  AiProficiencyPageEmptyState,
  AiSpendPageEmptyState,
  AiSpendTeamPageEmptyState,
  AiSpendMarketPageEmptyState,
  AiSpendTenurePageEmptyState,
  MarketPageEmptyState,
  TeamPageEmptyState,
  TenurePageEmptyState,
  UpskillingPageEmptyState,
  ModelSavingsPageEmptyState,
} from '@/domains/pages/empty-states/create-page-empty-state';
import { toPageNavKey } from '@/domains/pages/workspace-nav.utils';
import type { NebulyPageDefinition, NebulyPageKey } from '@/domains/pages/types';
import type { ProjectConfiguration } from '@/api/generated/model';
import { isReportSubproductEnabled } from '@/domains/reports/utils/report-subproduct-availability.utils';
import type { ReportSubproduct } from '@/domains/reports/utils/report-subproduct.utils';

export const AI_PROFICIENCY_PAGE: NebulyPageDefinition = {
  key: 'ai-proficiency',
  route: NEBULY_PAGE_ROUTES['ai-proficiency'],
  navKey: toPageNavKey('ai-proficiency'),
  title: 'AI Proficiency',
  description:
    'Measure how good your users are at using AI to do tasks. Scored 0–100 on six behaviors over the last 30 days, and segmented into four groups from the most expert to the novices.',
  icon: GraduationCap,
  defaultRank: 0,
  subproduct: 'aifi',
  Body: AiProficiencyPageBody,
  Loading: AiProficiencyPageLoadingSkeleton,
  EmptyState: AiProficiencyPageEmptyState,
};

export const TEAM_PAGE: NebulyPageDefinition = {
  key: 'team',
  route: NEBULY_PAGE_ROUTES.team,
  navKey: toPageNavKey('team'),
  title: 'Team',
  description:
    'Compare a team against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Users,
  defaultRank: 1,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('team'),
  EmptyState: TeamPageEmptyState,
};

export const MARKET_PAGE: NebulyPageDefinition = {
  key: 'market',
  route: NEBULY_PAGE_ROUTES.market,
  navKey: toPageNavKey('market'),
  title: 'Market',
  description:
    'Compare a market against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Globe,
  defaultRank: 2,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('market'),
  EmptyState: MarketPageEmptyState,
};

export const TENURE_PAGE: NebulyPageDefinition = {
  key: 'tenure',
  route: NEBULY_PAGE_ROUTES.tenure,
  navKey: toPageNavKey('tenure'),
  title: 'Tenure',
  description:
    'Compare a tenure band against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Clock,
  defaultRank: 3,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('tenure'),
  EmptyState: TenurePageEmptyState,
};


export const AI_PROFICIENCY_TEAM_PAGE: NebulyPageDefinition = {
  key: 'ai-proficiency-team',
  route: NEBULY_PAGE_ROUTES['ai-proficiency-team'],
  navKey: toPageNavKey('ai-proficiency-team'),
  title: 'Team',
  description:
    'Compare a team against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Users,
  defaultRank: 1,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('ai-proficiency-team'),
  EmptyState: TeamPageEmptyState,
};

export const AI_PROFICIENCY_MARKET_PAGE: NebulyPageDefinition = {
  key: 'ai-proficiency-market',
  route: NEBULY_PAGE_ROUTES['ai-proficiency-market'],
  navKey: toPageNavKey('ai-proficiency-market'),
  title: 'Market',
  description:
    'Compare a market against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Globe,
  defaultRank: 2,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('ai-proficiency-market'),
  EmptyState: MarketPageEmptyState,
};

export const AI_PROFICIENCY_TENURE_PAGE: NebulyPageDefinition = {
  key: 'ai-proficiency-tenure',
  route: NEBULY_PAGE_ROUTES['ai-proficiency-tenure'],
  navKey: toPageNavKey('ai-proficiency-tenure'),
  title: 'Tenure',
  description:
    'Compare a tenure band against the rest of the organization to see how well its users use AI. Look at the proficiency score, the four groups, and how people moved between them.',
  icon: Clock,
  defaultRank: 3,
  subproduct: 'aifi',
  Body: AiProficiencySegmentPageBody,
  Loading: createAiProficiencySegmentPageLoadingSkeleton('ai-proficiency-tenure'),
  EmptyState: TenurePageEmptyState,
};

export const UPSKILLING_PAGE: NebulyPageDefinition = {
  key: 'upskilling',
  route: NEBULY_PAGE_ROUTES.upskilling,
  navKey: toPageNavKey('upskilling'),
  title: 'Upskilling',
  description:
    'See where the main behavior gaps are and which one to work on to move users from one proficiency group to the next.',
  icon: GraduationCap,
  defaultRank: 4,
  subproduct: 'aifi',
  Body: UpskillingPageBody,
  Loading: UpskillingPageLoadingSkeleton,
  EmptyState: UpskillingPageEmptyState,
};

export const AI_SPEND_PAGE: NebulyPageDefinition = {
  key: 'ai-spend',
  route: NEBULY_PAGE_ROUTES['ai-spend'],
  navKey: toPageNavKey('ai-spend'),
  title: 'AI Spend',
  description:
    'See exactly where your AI budget goes, measured per task instead of per token. Track AI spend over any timeframe and break it down by task, team, market, or tenure to understand who is spending, on what, and where.',
  icon: Wallet,
  defaultRank: 0,
  subproduct: 'roi',
  filterBar: true,
  Body: AiSpendPageBody,
  Loading: AiSpendPageLoadingSkeleton,
  EmptyState: AiSpendPageEmptyState,
};


export const AI_SPEND_TEAM_PAGE: NebulyPageDefinition = {
  key: 'ai-spend-team',
  route: NEBULY_PAGE_ROUTES['ai-spend-team'],
  navKey: toPageNavKey('ai-spend-team'),
  title: 'Team',
  description:
    'Compare a team against the rest of the organization to see where AI spend goes. Track total spend, spend per task, and how costs trend over time.',
  icon: Users,
  defaultRank: 1,
  subproduct: 'roi',
  filterBar: true,
  Body: AiSpendSegmentPageBody,
  Loading: createAiSpendSegmentPageLoadingSkeleton('ai-spend-team'),
  EmptyState: AiSpendTeamPageEmptyState,
};

export const AI_SPEND_MARKET_PAGE: NebulyPageDefinition = {
  key: 'ai-spend-market',
  route: NEBULY_PAGE_ROUTES['ai-spend-market'],
  navKey: toPageNavKey('ai-spend-market'),
  title: 'Market',
  description:
    'Compare a market against the rest of the organization to see where AI spend goes. Track total spend, spend per task, and how costs trend over time.',
  icon: Globe,
  defaultRank: 2,
  subproduct: 'roi',
  filterBar: true,
  Body: AiSpendSegmentPageBody,
  Loading: createAiSpendSegmentPageLoadingSkeleton('ai-spend-market'),
  EmptyState: AiSpendMarketPageEmptyState,
};

export const AI_SPEND_TENURE_PAGE: NebulyPageDefinition = {
  key: 'ai-spend-tenure',
  route: NEBULY_PAGE_ROUTES['ai-spend-tenure'],
  navKey: toPageNavKey('ai-spend-tenure'),
  title: 'Tenure',
  description:
    'Compare a tenure band against the rest of the organization to see where AI spend goes. Track total spend, spend per task, and how costs trend over time.',
  icon: Clock,
  defaultRank: 3,
  subproduct: 'roi',
  filterBar: true,
  Body: AiSpendSegmentPageBody,
  Loading: createAiSpendSegmentPageLoadingSkeleton('ai-spend-tenure'),
  EmptyState: AiSpendTenurePageEmptyState,
};

export const MODEL_SAVINGS_PAGE: NebulyPageDefinition = {
  key: 'model-savings',
  route: NEBULY_PAGE_ROUTES['model-savings'],
  navKey: toPageNavKey('model-savings'),
  title: 'Model savings',
  description:
    'See which tasks are using models that cost more than necessary, and how much AI spend you could save by switching each task to the most cost-efficient model that delivers comparable quality.',
  icon: DollarSign,
  defaultRank: 5,
  subproduct: 'roi',
  Body: ModelSavingsPageBody,
  Loading: ModelSavingsPageLoadingSkeleton,
  EmptyState: ModelSavingsPageEmptyState,
};

const PAGE_DEFINITIONS: NebulyPageDefinition[] = [
  AI_PROFICIENCY_PAGE,
  TEAM_PAGE,
  MARKET_PAGE,
  TENURE_PAGE,
  AI_PROFICIENCY_TEAM_PAGE,
  AI_PROFICIENCY_MARKET_PAGE,
  AI_PROFICIENCY_TENURE_PAGE,
  UPSKILLING_PAGE,
  AI_SPEND_PAGE,
  AI_SPEND_TEAM_PAGE,
  AI_SPEND_MARKET_PAGE,
  AI_SPEND_TENURE_PAGE,
  MODEL_SAVINGS_PAGE,
];

const PAGE_BY_KEY = new Map<NebulyPageKey, NebulyPageDefinition>(PAGE_DEFINITIONS.map((page) => [page.key, page]));

export function getNebulyPageDefinition(pageKey: NebulyPageKey): NebulyPageDefinition {
  return PAGE_BY_KEY.get(pageKey)!;
}

export function getEnabledNebulyPages(
  subproduct: ReportSubproduct,
  config: Pick<ProjectConfiguration, 'roi_settings' | 'aifi_settings'> | undefined,
): NebulyPageDefinition[] {
  if (!isReportSubproductEnabled(subproduct, config)) return [];
  return PAGE_DEFINITIONS.filter((page) => page.subproduct === subproduct);
}
